import React, { useMemo, useCallback } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import type { SortDescriptor } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { getOrderCellData } from "./OrderRow";
import { getGroupCellData } from "./OrderGroupRow";
import type { Order, OrderGroup, ColumnDef, Entity } from "./types";

interface OrdersTableProps {
  paginatedGroups: OrderGroup[];
  headerColumns: ColumnDef[];
  expandedGroups: Set<string>;
  toggleGroup: (jobID_parent: string) => void;
  entities: Entity[];
  isDataReadOnlyOperators: boolean;
  setUnreadOrders: React.Dispatch<React.SetStateAction<string[]>>;
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
}

const TABLE_CLASSNAMES = {
  wrapper: ["max-h-[382px]", "max-w-3xl"],
  table: ["border-separate", "border-spacing-y-2"],
  th: ["bg-transparent", "text-default-500", "border-b", "border-divider"],
  td: [
    "bg-white",
    "p-3",
    "group-data-[first=true]:first:before:rounded-none",
    "group-data-[first=true]:last:before:rounded-none",
    "group-data-[middle=true]:before:rounded-none",
    "group-data-[last=true]:first:before:rounded-none",
    "group-data-[last=true]:last:before:rounded-none",
  ],
  tr: [
    "[&.group-child-row]:border-spacing-y-0",
    "[&.group-child-row+.group-child-row]:mt-0",
  ],
};

export function OrdersTable({
  paginatedGroups,
  headerColumns,
  expandedGroups,
  toggleGroup,
  entities,
  isDataReadOnlyOperators,
  setUnreadOrders,
  sortDescriptor,
  onSortChange,
}: Readonly<OrdersTableProps>) {
  const { t } = useTranslation();

  const tableClassNames = useMemo(
    () => ({
      ...TABLE_CLASSNAMES,
      table: ["border-separate", "border-spacing-y-0", "table-fixed", "w-full"],
      tbody: ["hidden"],
    }),
    []
  );

  const contentTableClassNames = useMemo(
    () => ({
      ...TABLE_CLASSNAMES,
      table: ["border-separate", "border-spacing-y-0", "table-fixed", "w-full"],
    }),
    []
  );

  const childTableClassNames = useMemo(
    () => ({
      ...TABLE_CLASSNAMES,
      table: ["border-separate", "border-spacing-y-0", "table-fixed", "w-full"],
      tr: [...(TABLE_CLASSNAMES.tr || []), "cursor-pointer", "group"],
      td: [...(TABLE_CLASSNAMES.td || []), "group-hover:!bg-default-200", "transition-colors"],
    }),
    []
  );

  // Cell render functions using useCallback for HeroUI compatibility
  const renderOrderCellCallback = useCallback(
    (order: Order, columnKey: React.Key, isChildRow = false) => {
      const { content, className, style } = getOrderCellData({
        order,
        columnKey: columnKey as string,
        columns: headerColumns,
        entities,
        isDataReadOnlyOperators,
        setUnreadOrders,
        isChildRow,
        t,
      });
      return (
        <TableCell className={className} style={style}>
          {content}
        </TableCell>
      );
    },
    [headerColumns, entities, isDataReadOnlyOperators, setUnreadOrders, t]
  );

  const renderGroupCellCallback = useCallback(
    (group: OrderGroup, columnKey: React.Key, isExpanded: boolean, onToggle: () => void) => {
      const { content, style } = getGroupCellData({
        group,
        columnKey: columnKey as string,
        columns: headerColumns,
        entities,
        isExpanded,
        onToggle,
        t,
      });
      return (
        <TableCell style={style}>
          {content}
        </TableCell>
      );
    },
    [headerColumns, entities, t]
  );

  return (
    <>
      {/* Table Header */}
      <Table
        isCompact
        removeWrapper
        aria-label="Orders Header"
        classNames={tableClassNames}
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === "actions" ? "center" : "start"}
              allowsSorting={column.sortable}
              style={column.style}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody>{[]}</TableBody>
      </Table>

      {/* Table Content */}
      <div className="flex flex-col gap-2 mt-2">
        {paginatedGroups.length === 0 && (
          <div className="text-center py-8 text-default-400">{t("no_orders")}</div>
        )}

        {paginatedGroups.map((group) => {
          const isMultipleOrders = group.orders.length > 1;
          const isExpanded = expandedGroups.has(group.jobID_parent);

          if (!isMultipleOrders) {
            // Single order
            const order = group.orders[0];
            return (
              <Table
                key={order.jobID}
                isCompact
                removeWrapper
                hideHeader
                aria-label={`Order ${order.jobID}`}
                classNames={contentTableClassNames}
              >
                <TableHeader columns={headerColumns}>
                  {(column) => (
                    <TableColumn key={column.uid} style={column.style}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody>
                  <TableRow key={order.jobID}>
                    {(columnKey) => renderOrderCellCallback(order, columnKey)}
                  </TableRow>
                </TableBody>
              </Table>
            );
          }

          // Multiple orders 
          return (
            <div key={`group-${group.jobID_parent}`} className="bg-white rounded-xl">
              <Table
                isCompact
                removeWrapper
                hideHeader
                aria-label={`Group ${group.jobID_parent}`}
                classNames={contentTableClassNames}
              >
                <TableHeader columns={headerColumns}>
                  {(column) => (
                    <TableColumn key={column.uid} style={column.style}>
                      {column.name}
                    </TableColumn>
                  )}
                </TableHeader>
                <TableBody>
                  <TableRow
                    key={`group-row-${group.jobID_parent}`}
                    className="cursor-pointer"
                    onClick={() => toggleGroup(group.jobID_parent)}
                  >
                    {(columnKey) =>
                      renderGroupCellCallback(
                        group,
                        columnKey,
                        isExpanded,
                        () => toggleGroup(group.jobID_parent)
                      )
                    }
                  </TableRow>
                </TableBody>
              </Table>

              {/* Expanded child orders */}
              {isExpanded && (
                <div className="pl-6 relative">
                  <div
                    className="absolute left-[27px] top-[-12px] w-0.5 bg-vistagrey/50 rounded-full z-10"
                    style={{ height: `calc(100% - 0.9rem)` }}
                  />
                  {group.orders.map((order, index) => {
                    const isLastChild = index === group.orders.length - 1;
                    return (
                      <div
                        key={order.jobID}
                        className={`relative ml-1 ${isLastChild ? "" : "border-b border-dashed border-default-300"}`}
                      >
                        <div className="absolute top-1/2 w-2 h-0.5 bg-vistagrey/50 z-10" />
                        <Table
                          isCompact
                          removeWrapper
                          hideHeader
                          aria-label={`Order ${order.jobID}`}
                          classNames={childTableClassNames}
                        >
                          <TableHeader columns={headerColumns}>
                            {(column) => (
                              <TableColumn key={column.uid} style={column.style}>
                                {column.name}
                              </TableColumn>
                            )}
                          </TableHeader>
                          <TableBody>
                            <TableRow key={order.jobID}>
                              {(columnKey) => renderOrderCellCallback(order, columnKey, true)}
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default OrdersTable;
